import { Broadcast } from '@goldcast/api/backend';
import HLS from 'hls.js';
import { AutoplayErrorDialog } from 'uicomponents';

import { LiveVideoRoomContext } from './actions';
import { Asset } from '../sidebar';
import { addHlsGuard } from '../simuliveroom/utils';

import analytics from '@/analytics';
import { appMode } from '@/assets/utilfunctions';
import { AGORA, HLS_PLAYBACK, BROADCAST_END_ALLOWANCE_TIME } from '@/constants';
import DialogService from '@/services/dialogs/DialogService';
import { createHlsTrackers } from '@/services/hlsTrackers';
import { closeJoiningDelayNetworkErrorModal } from '@/services/liveMedia.service';
import loggerService from '@/services/logger.service';
import trackerService from '@/services/tracker.service';
import store from '@/store';
import { getServerAdjustedDateTimeNow } from '@/utils/dateTime';

// Module-level HLS metrics state for live-hybrid video-share.
// Persists across video assets played within the same session.
let liveHybridPreviousHlsLevel = -1;
const hlsTrackers = createHlsTrackers('liveHybridVideo');

// initializeVideoPlaybackInIOS re-enters from its own error handler while hlsVideoElm
// survives, so each pass must remove the previous listeners. The target is tracked too
// because destroyExistingHlsContext swaps the element.
let iosListenerTarget: HTMLVideoElement | null = null;
let iosLoadedMetadataHandler: (() => void) | null = null;
let iosErrorHandler: (() => void) | null = null;
let iosRetryTimeout: ReturnType<typeof setTimeout> | null = null;

export function removeIOSPlaybackListeners() {
  if (iosListenerTarget) {
    if (iosLoadedMetadataHandler) {
      iosListenerTarget.removeEventListener('loadedmetadata', iosLoadedMetadataHandler);
    }
    if (iosErrorHandler) {
      iosListenerTarget.removeEventListener('error', iosErrorHandler, true);
    }
  }
  iosLoadedMetadataHandler = null;
  iosErrorHandler = null;
  iosListenerTarget = null;

  if (iosRetryTimeout) {
    clearTimeout(iosRetryTimeout);
    iosRetryTimeout = null;
  }
}

export function clearVideoPlaybackStateTimeouts(context: LiveVideoRoomContext) {
  if (!context.state.videoPlaybackStartStateTimeout) {
    return;
  }
  clearTimeout(context.state.videoPlaybackStartStateTimeout);
  context.state.videoPlaybackStartStateTimeout = null;
}

export function shouldStartVideoPlayback(context: LiveVideoRoomContext) {
  if (!isCurrentRouteABroadcast()) {
    return false;
  }

  const broadcastData = getCurrentBroadcast(context);

  if (broadcastData?.broadcast_type !== 'LIVE_HYBRID') {
    return false;
  }

  const isLiteModeActive: boolean = isLiteModeActiveAndBeingUsed(context, broadcastData);

  if (isLiteModeActive) {
    return false;
  }

  if (!context.rootGetters['event/stage/showNewStageControls']) {
    const backstageChannel = `${broadcastData?.id}${AGORA.UID_SUFFIX.BACKSTAGE}`;
    const isConnectedToBackstage =
      context.rootState.mediaroom.mediaRooms[backstageChannel]?.desiredStatus === 'CONNECTED';
    if (isConnectedToBackstage) {
      return false;
    }
  }

  const videoAsset = getPlayingVideoAsset(broadcastData);

  if (
    !videoAsset ||
    !broadcastData?.playback_start_time ||
    (videoAsset?.id === context.state.currentPlayingVideoAsset?.id &&
      new Date(broadcastData?.playback_start_time).getTime() === context.state.startTimeOfVideoPlayback?.getTime())
  ) {
    return false;
  }

  return true;
}

export function isLiteModeActiveAndBeingUsed(context: LiveVideoRoomContext, broadcastData: Broadcast): boolean {
  const isLiteModeActive = context.rootState.user?.liteMode?.liteModeActive;
  const isLiteModeAvailable = broadcastData?.is_recording;

  const speakerIDs = broadcastData?.speakerIds || [];
  const user_id: string | undefined = context.rootState?.user?.currentUser?.id;
  const tvMode = context.rootState.event.stage?.isTVMode;
  const isSpeaker = user_id ? speakerIDs.includes(user_id) && !tvMode : false;
  const isOrganizer = context.rootGetters['event/isBroadcastOrganizer'];

  return !!(isLiteModeActive && isLiteModeAvailable && !(isSpeaker || isOrganizer));
}

export function initializeHLSJSVideoPlayback(context: LiveVideoRoomContext) {
  destroyExistingHlsContext(context);
  // Stamp the start time for this video asset — used to compute startup_time_ms
  trackerService.track('liveHybridVideo', 'shouldBeUsed', { isEnabled: true });
  trackerService.track('liveHybridVideo', 'connectionInitialized', { timestamp: Date.now() });
  liveHybridPreviousHlsLevel = -1;
  initializeHLSVideoRoom(context)
    .then(() => {
      playVideoUsingHLSJS(context);
    })
    .catch(err => {
      loggerService.error('Error initializing the hls video room for live broadcasts, switching to filler slide!', err);
      context.state.isFillerSlideVisible = true;
      context.dispatch('showHlsVideoPlaybackError');
    });
}

export function destroyExistingHlsContext(context: LiveVideoRoomContext) {
  if (!context.state.hlsContext) {
    return;
  }
  context.state.hlsContext.destroy();
  context.state.hlsVideoElm = document.createElement('video');
}

export function initializeHLSVideoRoom(context: LiveVideoRoomContext) {
  return new Promise((resolve, reject) => {
    try {
      if (!context.state.hlsVideoElm) {
        context.state.hlsVideoElm = document.createElement('video');
      }
      context.state.hlsVideoElm.setAttribute('webkit-playsinline', '');
      context.state.hlsVideoElm.playsInline = true;
      context.state.hlsVideoElm.webkitPlaysInline = true;
      context.state.hlsVideoElm.controls = false;
      context.state.hlsVideoElm.addEventListener('contextmenu', (event: Event) => {
        return event.preventDefault();
      });
      context.state.hlsVideoElm.muted = context.rootState.event.stage?.muted;
      const hls = new HLS({
        maxLoadingDelay: 4,
        initialLiveManifestSize: 1,
        maxStarvationDelay: 4,
        startLevel: 1,
        autoStartLoad: true,
        fragLoadingMaxRetry: HLS_PLAYBACK.FRAG_LOAD_MAX_RETRIES,
        manifestLoadingMaxRetry: HLS_PLAYBACK.MANIFEST_LOAD_MAX_RETRIES,
        levelLoadingMaxRetry: HLS_PLAYBACK.LEVEL_LOAD_MAX_RETIRES,
        maxMaxBufferLength: 30,
        abrBandWidthUpFactor: 0.6,
        xhrSetup: function (xhr, url) {
          addHlsGuard(url, xhr, context as any);
        }
      });
      hls.attachMedia(context.state.hlsVideoElm);
      hls.on(HLS.Events.MEDIA_ATTACHED, function () {
        context.state.hlsContext = hls;
        context.state.hlsFragmentsThatFailedToLoad = [];
        attachHLSEventListeners(context);
        resolve(null);
      });
      context.dispatch('setActiveSpeakerDevice');
    } catch (err) {
      reject(err);
    }
  });
}

export function attachHLSEventListeners(context: LiveVideoRoomContext) {
  if (context.state.hlsContext) {
    context.state.hlsContext.on(HLS.Events.LEVEL_LOADED, (eventName: string, data: any) => {
      handleLevelLoaded(context, data);
      if (context.state.isHLSLevelLoadError) {
        context.commit('resetIsHLSLevelLoadError');
      }
    });

    context.state.hlsContext.on(HLS.Events.FRAG_LOADED, (_event: any, data: any) => {
      const loadTime = (data?.stats?.loading?.end ?? 0) - (data?.stats?.loading?.start ?? 0);
      hlsTrackers.trackFragLoaded(loadTime);
      if (context.state.isHLSFragLoadError) {
        context.commit('resetIsHLSFragLoadError');
      }
    });

    context.state.hlsContext.on(HLS.Events.MANIFEST_LOADED, () => {
      if (context.state.isHLSManifestLoadError) {
        context.commit('resetIsHLSManifestLoadError');
      }
    });

    context.state.hlsContext.on(HLS.Events.LEVEL_SWITCHED, (_event: any, data: any) => {
      const toLevel = data.level;
      const isDowngrade = hlsTrackers.trackLevelSwitched(liveHybridPreviousHlsLevel, toLevel);
      analytics.track('live_hls_level_switched', {
        broadcast_id: store.state.route?.params?.id,
        from_level: liveHybridPreviousHlsLevel,
        to_level: toLevel,
        bitrate_bps: context.state.hlsContext?.levels?.[toLevel]?.bitrate ?? null,
        is_downgrade: isDowngrade
      });
      liveHybridPreviousHlsLevel = toLevel;
    });

    context.state.hlsContext.on(HLS.Events.FPS_DROP, (_event: any, data: any) => {
      hlsTrackers.trackFpsDrop(data.droppedLevel ?? 0);
      analytics.track('live_hls_fps_drop', {
        broadcast_id: store.state.route?.params?.id,
        dropped_frames: data.droppedLevel ?? 0,
        total_dropped_frames: data.totalDroppedFrames ?? 0
      });
    });

    addVideoPlaybackEndListener(context);
    if (context.state.hlsVideoElm) {
      context.state.hlsVideoElm.onwaiting = () => {
        const now = Date.now();
        hlsTrackers.trackBufferStart(now);
        analytics.track('live_hls_buffer_start', {
          broadcast_id: store.state.route?.params?.id,
          position_s: context.state.hlsVideoElm?.currentTime,
          current_bandwidth_bps: context.state.hlsContext?.bandwidthEstimate ?? null
        });
        handleVideoPlaybackStall(context);
      };

      context.state.hlsVideoElm.oncanplay = () => {
        hlsTrackers.trackBufferEnd();
        handleVideoPlaybackPreResume(context);
      };

      context.state.hlsVideoElm.onplay = () => {
        context.state.hlsVideoElm.controls = false;
      };

      context.state.hlsVideoElm.onplaying = () => {
        context.state.retriesDoneForFragLoadingError = 0;
        context.state.retriesDoneForLevelLoadingError = 0;
        context.state.retriesDoneForManifestLoadingError = 0;
        context.state.hlsVideoElm.controls = false;
        // bufferEnd safety net + startup time recording
        hlsTrackers.trackBufferEnd();
        hlsTrackers.trackConnectionEstablished();
        closeJoiningDelayNetworkErrorModal();
      };
    }

    context.state.hlsContext.on(HLS.Events.ERROR, (errorName: string, errorDetails: any) => {
      if (!errorDetails?.fatal) {
        hlsTrackers.trackNonFatalError();
      } else {
        analytics.track('live_hls_error', {
          broadcast_id: store.state.route?.params?.id,
          error_type: errorDetails.type,
          error_details: errorDetails.details,
          is_fatal: true
        });
      }
      handleHLSJSErrors(context, errorDetails);
    });
  }
}

// Sets up tracking for RTMP (LIVE_EXTERNAL) HLS streams. Reuses liveHybridVideo
// tracker since LIVE_HYBRID and LIVE_EXTERNAL are mutually exclusive per broadcast.
export function initializeRtmpHlsTracking(context: LiveVideoRoomContext, hlsInstance: any) {
  context.state.rtmpHlsContext = hlsInstance;
  trackerService.track('liveHybridVideo', 'shouldBeUsed', { isEnabled: true });
  trackerService.track('liveHybridVideo', 'connectionInitialized', { timestamp: Date.now() });

  let previousLevel = -1;
  let firstFragLoaded = false;

  hlsInstance.on(HLS.Events.FRAG_LOADED, (_event: any, data: any) => {
    hlsTrackers.trackFragLoaded((data?.stats?.loading?.end ?? 0) - (data?.stats?.loading?.start ?? 0));
    if (!firstFragLoaded) {
      firstFragLoaded = true;
      hlsTrackers.trackConnectionEstablished();
    }
  });

  hlsInstance.on(HLS.Events.LEVEL_SWITCHED, (_event: any, data: any) => {
    hlsTrackers.trackLevelSwitched(previousLevel, data.level);
    previousLevel = data.level;
  });

  hlsInstance.on(HLS.Events.FPS_DROP, (_event: any, data: any) => {
    hlsTrackers.trackFpsDrop(data.droppedLevel ?? 0);
  });

  hlsInstance.on(HLS.Events.ERROR, (_event: any, errorDetails: any) => {
    if (!errorDetails?.fatal) {
      hlsTrackers.trackNonFatalError();
    }
  });
}

export function handleLevelLoaded(context: LiveVideoRoomContext, data: any) {
  if (!context.state.isWaitingForVideoLoadToStart) {
    return;
  }
  context.state.totalDurationOfPlayingVideo = data.details.totalduration * 1000;
  context.state.isWaitingForVideoLoadToStart = false;
  startPlaybackOfFillerSlideOrHLS(context);
}

export function skipToGivenVideoDuration(context: LiveVideoRoomContext, duration: number) {
  const currentVideoTime = duration ? Math.floor(duration / 1000) : 0;
  context.state.hlsVideoElm.currentTime = currentVideoTime;
  if (HLS.isSupported()) {
    context.state.hlsContext.startLoad(currentVideoTime);
  }
}

export function getIdealPlaybackDuration(context: LiveVideoRoomContext) {
  const currentTime: Date = getServerAdjustedDateTimeNow();
  const playbackStartDateTime: Date = context.state.startTimeOfVideoPlayback || new Date();
  return currentTime.getTime() - playbackStartDateTime.getTime();
}

export function isUserAheadOfIdealPlaybackDuration(currentPlaybackDuration: number, idealPlaybackDuration: number) {
  return currentPlaybackDuration > idealPlaybackDuration + HLS_PLAYBACK.HLS_ALLOWED_PLAY_AHEAD_DURATION;
}

export function isUserFallingBehindIdealPlaybackDuration(
  currentPlaybackDuration: number,
  idealPlaybackDuration: number
) {
  return currentPlaybackDuration < idealPlaybackDuration - HLS_PLAYBACK.HLS_PLAYBACK_MAX_FALLBACK_DURATION;
}

export function addVideoPlaybackEndListener(context: LiveVideoRoomContext) {
  context.state.hlsVideoElm.onended = (event: Event) => {
    const idealPlaybackDuration = getIdealPlaybackDuration(context);
    const totalDurationOfPlayingVideo = context.state.totalDurationOfPlayingVideo;
    if (idealPlaybackDuration >= totalDurationOfPlayingVideo) {
      doPostVideoPlaybackCleanup(context);
    }
  };
}

export function doPostVideoPlaybackCleanup(context: LiveVideoRoomContext) {
  destroyExistingHlsContext(context);
  clearTimeoutsAndIntervals(context);
  context.state.isFillerSlideVisible = true;
}

export function clearTimeoutsAndIntervals(context: LiveVideoRoomContext) {
  clearPlaybackStallTimeout(context);
  clearManifestLoadErrorTimeout(context);
  clearVideoSyncCheckerInterval(context);
  removeIOSPlaybackListeners();
}

export function resetVideoRoomState(context: LiveVideoRoomContext) {
  context.state.currentPlayingVideoAsset = null;
  context.state.totalDurationOfPlayingVideo = 0;
  context.state.startTimeOfVideoPlayback = null;
  context.state.playbackStartTimeOverride = null;
  context.state.isFillerSlideVisible = true;
  context.state.retriesDoneForLevelLoadingError = 0;
  context.state.retriesDoneForFragLoadingError = 0;
  context.state.retriesDoneForManifestLoadingError = 0;
}

export function handleVideoPlaybackStall(context: LiveVideoRoomContext) {
  clearPlaybackStallTimeout(context);

  // If the player is waiting wait for some time before
  // showing the loader.
  context.state.timeOutToHandlePlaybackStall = setTimeout(() => {
    context.state.timeOutToHandlePlaybackStall = null;
    context.state.hasVideoBufferPlaybackStalled = true;
  }, context.state.timeToWaitBeforeShowingLoading);
}

export function clearPlaybackStallTimeout(context: LiveVideoRoomContext) {
  if (!context.state.timeOutToHandlePlaybackStall) {
    return;
  }
  clearTimeout(context.state.timeOutToHandlePlaybackStall);
  context.state.timeOutToHandlePlaybackStall = null;
}

export function handleVideoPlaybackPreResume(context: LiveVideoRoomContext) {
  context.state.hasVideoBufferPlaybackStalled = false;
  clearPlaybackStallTimeout(context);
}

export function startPlaybackOfFillerSlideOrHLS(context: LiveVideoRoomContext) {
  const idealPlaybackDuration = getIdealPlaybackDuration(context);
  const totalVideoLength = context.state.totalDurationOfPlayingVideo;
  context.state.isFillerSlideVisible = true;
  if (idealPlaybackDuration < totalVideoLength) {
    if (idealPlaybackDuration < HLS_PLAYBACK.HLS_PLAYBACK_MAX_FALLBACK_DURATION / 2) {
      setInitialHlsPlaybackStartTime(context, 0);
    } else {
      setInitialHlsPlaybackStartTime(context, idealPlaybackDuration);
    }

    playVideoPlayer(context);
    setupVideoSyncChecker(context);
  }
}

export function setInitialHlsPlaybackStartTime(context: LiveVideoRoomContext, playbackDuration: number) {
  if (context.state.playbackStartTimeOverride) {
    skipToGivenVideoDuration(context, context.state.playbackStartTimeOverride);
    context.state.playbackStartTimeOverride = null;
  } else {
    skipToGivenVideoDuration(context, playbackDuration);
  }
}

export function playVideoUsingHLSJS(context: LiveVideoRoomContext) {
  context.state.isFillerSlideVisible = true;
  context.state.currentPlayingVideoAsset = null;
  clearVideoSyncCheckerInterval(context);

  const broadcastData = getCurrentBroadcast(context);
  const videoAsset = getPlayingVideoAsset(broadcastData);
  if (!videoAsset) {
    return;
  }

  addVideoSourceToHLSContext(context, videoAsset, new Date(broadcastData?.playback_start_time || ''));
}

export function isCurrentRouteABroadcast() {
  const isLiveStreamRoute = appMode === 'liveStream';
  return store.state.route?.params?.id && store.state.route?.fullPath?.includes('/broadcast') && !isLiveStreamRoute;
}

export function getCurrentBroadcast(context: LiveVideoRoomContext): Broadcast {
  const broadcastID = store.state.route?.params?.id;
  return context.rootGetters['event/currentEventBroadcast'](broadcastID);
}

export function getPlayingVideoAsset(broadcastData: Broadcast): Asset | undefined {
  if (broadcastData?.current_playing_video_asset) {
    return broadcastData.video_assets?.find(
      (video_asset: Asset) => video_asset.id === broadcastData.current_playing_video_asset
    );
  }
}

export function addVideoSourceToHLSContext(context: LiveVideoRoomContext, videoAsset: Asset, playbackStartTime: Date) {
  // play video asset on stage
  context.state.currentPlayingVideoAsset = videoAsset;
  const videoLocation = getHlsVideoURL(context, videoAsset);
  context.state.hlsVideoURL = videoLocation;
  context.state.startTimeOfVideoPlayback = playbackStartTime;
  context.state.isWaitingForVideoLoadToStart = true;
  context.state.hlsContext.loadSource(videoLocation);
}

export function getHlsVideoURL(context: LiveVideoRoomContext, videoAsset: Asset) {
  if (videoAsset?.client_hls_path) {
    return videoAsset.client_hls_path;
  }

  if (videoAsset?.hls_path) {
    return videoAsset.hls_path;
  }

  if (videoAsset?.fallback_hls_path) {
    return videoAsset.fallback_hls_path;
  }

  const videoStreamLocation = context.getters.videoStreamingLocation;
  return (
    videoAsset?.urls?.hls ||
    `${videoStreamLocation}/organization/${videoAsset.organization}/videos/${videoAsset.id}/hls.m3u8`
  );
}

export function playVideoPlayer(context: LiveVideoRoomContext) {
  if (!context.state.hlsVideoElm) {
    return;
  }
  context.state.hlsVideoElm
    .play()
    .catch(() => {
      if (context.rootState.event.stage?.muted) {
        return;
      }
      context.commit('event/stage/toggleMuted', null, {
        root: true
      });
      context.state.hlsVideoElm.muted = true;
      context.state.hlsVideoElm
        .play()
        .then(() => {
          const res = DialogService.show(AutoplayErrorDialog);
          res.promise
            .then(() => {
              if (context.rootState.event.stage?.muted) {
                context.commit('event/stage/toggleMuted', null, {
                  root: true
                });
              }
            })
            .catch(err => {
              loggerService.error('Error while toggeling mute state!', err);
            });
        })
        .catch(err => {
          loggerService.error('Error while playing video on mute!', err);
          context.commit('event/stage/toggleMuted', null, {
            root: true
          });
          context.state.hlsVideoElm.controls = true;
          context.state.hlsVideoElm.poster = context.getters['fillerSlideUrl'];
        });
    })
    .finally(() => {
      context.state.isFillerSlideVisible = false;
    });
}

export function handleHLSJSErrors(context: LiveVideoRoomContext, errorDetails: any) {
  const errorMessage = `
      Hls player encountered an error!,
      IS fatal: ${errorDetails?.fatal},
      Error details: 
    `;
  loggerService.warn(errorMessage, errorDetails);

  if (!errorDetails?.fatal) {
    loggerService.warn('Hls player encounted a non-fatal error!', errorDetails);
    return;
  }

  if (errorDetails.type === HLS.ErrorTypes.NETWORK_ERROR) {
    handleHlsNetworkError(context, errorDetails);
  } else if (errorDetails.type === HLS.ErrorTypes.MEDIA_ERROR) {
    handleMediaErrors(context, errorDetails);
  } else {
    loggerService.error("Couldn't recover from hls error! Destroying the component.");
    context.state.hlsContext.destroy();
    context.dispatch('showHlsVideoPlaybackError');
  }
}

export function handleHlsNetworkError(context: LiveVideoRoomContext, errorDetails: any) {
  loggerService.warn('Fatal network error encountered, trying to recover!');
  if (errorDetails?.details === HLS.ErrorDetails.LEVEL_LOAD_ERROR) {
    tryRecoveringAfterLevelLoadError(context);
  } else if (errorDetails?.details === HLS.ErrorDetails.MANIFEST_LOAD_ERROR) {
    handleManifestLoadError(context);
  } else if (errorDetails?.details === HLS.ErrorDetails.FRAG_LOAD_ERROR) {
    handleFatalFragLoadingError(context, errorDetails);
  }

  if (errorDetails?.response?.code === 404) {
    return;
  }

  if ([HLS.ErrorDetails.LEVEL_LOAD_ERROR, HLS.ErrorDetails.LEVEL_LOAD_TIMEOUT].includes(errorDetails?.details)) {
    context.commit('setIsHLSFragLoadError', true);
    return;
  }

  if ([HLS.ErrorDetails.MANIFEST_LOAD_ERROR, HLS.ErrorDetails.MANIFEST_LOAD_TIMEOUT].includes(errorDetails?.details)) {
    context.commit('setIsHLSManifestLoadError', true);
    return;
  }

  if ([HLS.ErrorDetails.FRAG_LOAD_ERROR, HLS.ErrorDetails.FRAG_LOAD_TIMEOUT].includes(errorDetails?.details)) {
    context.commit('setIsHLSFragLoadError', true);
    return;
  }
}

export function tryRecoveringAfterLevelLoadError(context: LiveVideoRoomContext) {
  if (context.state.retriesDoneForLevelLoadingError >= HLS_PLAYBACK.LEVEL_LOAD_MAX_RETIRES) {
    context.dispatch('showHlsVideoPlaybackError');
  }
  const idealPlaybackDuration = getIdealPlaybackDuration(context);
  context.state.hlsContext.startLoad(Math.floor(idealPlaybackDuration / 1000));
  context.state.retriesDoneForLevelLoadingError = context.state.retriesDoneForLevelLoadingError + 1;
}

export function handleManifestLoadError(context: LiveVideoRoomContext) {
  clearManifestLoadErrorTimeout(context);
  context.state.timeoutToHanldeManifiestLoadErrors = setTimeout(() => {
    context.state.hlsContext.loadSource(context.state.hlsVideoURL);
  }, context.state.timeToWaitBeforeRetryingLevelLoad);
}

export function clearManifestLoadErrorTimeout(context: LiveVideoRoomContext) {
  if (!context.state.timeoutToHanldeManifiestLoadErrors) {
    return;
  }
  clearTimeout(context.state.timeoutToHanldeManifiestLoadErrors);
  context.state.timeoutToHanldeManifiestLoadErrors = null;
}

export function handleFatalFragLoadingError(context: LiveVideoRoomContext, errorDetails: any) {
  loggerService.error('A frag failed loading even 6 after retries!', 'Frag url:', errorDetails?.frag?.relurl);
  context.state.hlsFragmentsThatFailedToLoad.push(errorDetails.frag);
  if (context.state.currentPlaybackDuration === errorDetails.frag.start) {
    nudgePlaybackForwardToNextFrag(context, errorDetails.frag);
  }
}

export function nudgePlaybackForwardToNextFrag(context: LiveVideoRoomContext, frag: any) {
  try {
    if (parseFloat(frag?.start) > -1 && parseFloat(frag?.duration) > -1) {
      context.state.playbackStartTimeOverride = (parseFloat(frag?.start) + parseFloat(frag?.duration) + 0.1) * 1000;

      setTimeout(() => {
        playVideoUsingHLSJS(context);
      }, 2000);
    }
  } catch (err) {
    loggerService.error('Error while nudging the plaback forward by one chunk duration!', err);
  }
}

export function handleMediaErrors(context: LiveVideoRoomContext, errorDetails: any) {
  if (errorDetails?.details === HLS.ErrorDetails.BUFFER_STALLED_ERROR) {
    loggerService.error(
      'New buffer addition might have stopped because of frag load error!',
      'Checking and trying to recover'
    );

    const fragThatStalledPlayback = (context.state.hlsFragmentsThatFailedToLoad || []).find(frag => {
      return frag?.start == context.state.currentPlaybackDuration;
    });

    if (fragThatStalledPlayback) {
      nudgePlaybackForwardToNextFrag(context, fragThatStalledPlayback);
    }
  } else {
    loggerService.warn('Fatal media error encountered, trying to recover!');
    context.state.hlsContext.recoverMediaError();
  }
}

export function initializeVideoPlaybackInIOS(context: LiveVideoRoomContext) {
  const broadcastData = getCurrentBroadcast(context);
  const videoAsset = getPlayingVideoAsset(broadcastData);
  context.state.isFillerSlideVisible = true;

  if (!videoAsset || !context.state.hlsVideoElm) {
    removeIOSPlaybackListeners();
    return;
  }

  clearVideoSyncCheckerInterval(context);
  context.state.currentPlayingVideoAsset = videoAsset;
  context.state.startTimeOfVideoPlayback = new Date(broadcastData.playback_start_time || '');
  const videoLocation = getHlsVideoURL(context, videoAsset);
  context.state.hlsVideoElm.setAttribute('playsinline', '');
  context.state.hlsVideoElm.src = videoLocation;
  context.state.hlsVideoElm.muted = context.rootState.event.stage?.muted;

  removeIOSPlaybackListeners();

  iosListenerTarget = context.state.hlsVideoElm;
  iosLoadedMetadataHandler = () => {
    playVideoInIOS(context);
  };
  iosErrorHandler = () => {
    if (iosRetryTimeout) {
      clearTimeout(iosRetryTimeout);
    }
    iosRetryTimeout = setTimeout(() => {
      iosRetryTimeout = null;
      initializeVideoPlaybackInIOS(context);
    }, 6000);
  };

  context.state.hlsVideoElm.addEventListener('loadedmetadata', iosLoadedMetadataHandler);
  context.state.hlsVideoElm.addEventListener('error', iosErrorHandler, true);
}

export function playVideoInIOS(context: LiveVideoRoomContext) {
  context.state.totalDurationOfPlayingVideo = context.state.hlsVideoElm.duration * 1000;
  const idealPlaybackDuration = getIdealPlaybackDuration(context);

  if (idealPlaybackDuration < context.state.totalDurationOfPlayingVideo) {
    if (idealPlaybackDuration < HLS_PLAYBACK.HLS_PLAYBACK_MAX_FALLBACK_DURATION / 2) {
      skipToGivenVideoDuration(context, 0);
    } else {
      skipToGivenVideoDuration(context, idealPlaybackDuration);
    }
    playVideoPlayer(context);
    setupVideoSyncChecker(context);
    addVideoPlaybackEndListener(context);
  }
}

export function clearVideoSyncCheckerInterval(context: LiveVideoRoomContext) {
  if (context.state.intervalForVideoPlaybackSync) {
    clearInterval(context.state.intervalForVideoPlaybackSync);
  }
}

export function setupVideoSyncChecker(context: LiveVideoRoomContext) {
  clearVideoSyncCheckerInterval(context);
  context.state.intervalForVideoPlaybackSync = setInterval(() => {
    resyncUserVideoPlayerToIdealPlayback(context);
  }, HLS_PLAYBACK.HLS_PLAYBACK_SYNC_INTERVAL);
}

export function resyncUserVideoPlayerToIdealPlayback(context: LiveVideoRoomContext) {
  const idealPlaybackDuration = getIdealPlaybackDuration(context);
  const currentPlaybackDuration = context.state.hlsVideoElm.currentTime * 1000;
  const totalDurationOfPlayingVideo = context.state.totalDurationOfPlayingVideo;

  context.state.currentPlaybackDuration = currentPlaybackDuration;
  if (
    isUserAheadOfIdealPlaybackDuration(currentPlaybackDuration, idealPlaybackDuration) ||
    isUserFallingBehindIdealPlaybackDuration(currentPlaybackDuration, idealPlaybackDuration)
  ) {
    if (idealPlaybackDuration >= totalDurationOfPlayingVideo) {
      loggerService.info(
        'The user is falling behind the ideal playback time, while trying to sync him, the ideal time is more than video duration so ending playback!'
      );
      doPostVideoPlaybackCleanup(context);
    } else {
      loggerService.info('User is falling behind the ideal playback time. Syncing user to the ideal playback time!');
      trackerService.track('liveHybridVideo', 'syncCorrectionCount');
      analytics.track('live_hls_sync_correction', {
        broadcast_id: store.state.route?.params?.id,
        current_position_ms: currentPlaybackDuration,
        ideal_position_ms: idealPlaybackDuration,
        drift_ms: Math.abs(currentPlaybackDuration - idealPlaybackDuration)
      });
      if (context.state.hlsVideoElm.paused) {
        playVideoPlayer(context);
      }
      skipToGivenVideoDuration(context, idealPlaybackDuration);
    }
  }
}

export function isBroadcastRunning(context: LiveVideoRoomContext, broadcastData: Broadcast) {
  const isUserRecorder = !!store.state.route?.query?.record_mode;
  const isBroadcastLive = broadcastData?.is_live || false;
  const broadcastStartDateTime = broadcastData?.start_time ? new Date(broadcastData?.start_time) : new Date();
  const broadcastStartTimeStamp = broadcastStartDateTime.getTime();

  const broadcastEndDateTime = broadcastData?.end_time ? new Date(broadcastData?.end_time) : new Date();
  const broadcastEndTimeStamp = broadcastEndDateTime.getTime() + BROADCAST_END_ALLOWANCE_TIME;

  const currentDateTime = context.rootState.event.stage?.heartBeatDate || new Date();
  const currentTimeStamp = currentDateTime.getTime();

  return (
    isBroadcastLive ||
    isUserRecorder ||
    (broadcastStartTimeStamp < currentTimeStamp && currentTimeStamp < broadcastEndTimeStamp)
  );
}
